import { makeArticle, notWorkingRelated } from "./learnFactory";

export const pageStillDepressedOnMedication = makeArticle({
  slug: "why-still-depressed-while-taking-medication",
  title:
    "Why Do I Still Feel Depressed While Taking Medication? | Neuro Wellness",
  description:
    "Why you may still feel depressed while taking antidepressants: inadequate dose, misdiagnosis, treatment-resistant depression, and what to do about it.",
  canonical: "/learn/why-still-depressed-while-taking-medication",
  h1: "Why Do I Still Feel Depressed While Taking Medication?",
  breadcrumbLabel: "Still Depressed on Medication",
  shortAnswer:
    "Feeling depressed while taking antidepressants can happen for several reasons: the dose may be too low, the medication may not be the right match for your brain, the trial may not have been long enough, an undiagnosed condition (like bipolar disorder) may be present, or your depression may be treatment-resistant. Treatment-resistant depression affects roughly one-third of people treated for major depressive disorder. Options include medication adjustments, TMS, SAINT® accelerated TMS, and SPRAVATO®. A psychiatric evaluation can identify why medication isn't working and what to try next.",
  keyTakeaways: [
    "Feeling depressed on medication is common, not unusual.",
    "The dose may be too low or the trial too short.",
    "Misdiagnosis (bipolar vs. unipolar) can cause non-response.",
    "Treatment-resistant depression affects ~1/3 of treated patients.",
    "TMS, SAINT®, and SPRAVATO® are options beyond medication.",
  ],
  sections: [
    {
      heading: "Common Reasons Medication Isn't Working",
      paragraphs: [
        "Several factors can cause persistent depression despite taking medication:",
      ],
      list: [
        "Inadequate dose — the medication was never increased to a therapeutic level",
        "Inadequate duration — the trial was too short (less than 6-8 weeks)",
        "Wrong medication — this specific drug isn't the right match for your brain",
        "Misdiagnosis — bipolar depression can be mistaken for unipolar depression",
        "Comorbid conditions — anxiety, PTSD, substance use, or medical issues",
        "Treatment-resistant depression — genuine non-response to standard treatments",
        "Non-adherence — missed doses or inconsistent use",
      ],
    },
    {
      heading: "What to Do",
      paragraphs: [
        "If you're still depressed while taking medication, talk to your prescriber. Options include adjusting the dose, switching to a different medication class, adding an augmenting medication, or considering non-medication treatments like TMS, SAINT®, or SPRAVATO®. A comprehensive psychiatric evaluation can identify the cause and recommend next steps.",
        "Never stop medication abruptly without supervision — discontinuation can cause withdrawal symptoms and raise relapse risk.",
      ],
    },
    {
      heading: "When to Consider Non-Medication Treatments",
      paragraphs: [
        "If you've tried multiple antidepressants at adequate doses and durations without success, non-medication treatments may be appropriate. TMS is FDA-cleared for depression that hasn't responded to medication. SAINT® is a 5-day accelerated protocol for treatment-resistant depression. SPRAVATO® is FDA-approved for treatment-resistant depression. These work through different mechanisms than oral antidepressants.",
      ],
    },
  ],
  faqs: [
    {
      question: "Why do I still feel depressed while taking medication?",
      answer:
        "Several reasons: the dose may be too low, the trial too short, the medication may not be the right match, an undiagnosed condition like bipolar disorder may be present, or your depression may be treatment-resistant. Talk to your prescriber about adjusting treatment or considering non-medication options.",
    },
    {
      question: "Is it normal to still feel depressed on antidepressants?",
      answer:
        "Yes. Roughly one-third of people treated for major depressive disorder do not reach remission after two treatment steps. This is called treatment-resistant depression. It's common and treatable — options include medication adjustments, TMS, SAINT®, and SPRAVATO®.",
    },
    {
      question: "What should I do if my antidepressant isn't working?",
      answer:
        "Talk to your prescriber. Options include adjusting the dose, switching medications, adding augmentation, or considering TMS, SAINT®, or SPRAVATO®. A psychiatric evaluation can identify the cause. Never stop medication abruptly without supervision.",
    },
    {
      question: "Can TMS help if I'm still depressed on medication?",
      answer:
        "Yes. TMS is FDA-cleared specifically for adults whose depression hasn't improved on antidepressant medication. It uses magnetic pulses, not medication, and doesn't cause systemic side effects. A psychiatric evaluation can determine if TMS is appropriate.",
    },
    {
      question: "Should I stop my medication if it isn't working?",
      answer:
        "No, not on your own. Stopping abruptly can cause discontinuation symptoms and raise relapse risk. Talk to your prescriber about a gradual taper and what to try next. Your prescriber can plan the transition safely.",
    },
  ],
  sources: [
    {
      citation:
        "Rush AJ, et al. Acute and longer-term outcomes in depressed outpatients requiring one or several treatment steps: a STAR*D report. Am J Psychiatry. 2006;163(11):1905-1917.",
      url: "https://pubmed.ncbi.nlm.nih.gov/17074942/",
    },
  ],
  relatedLinks: notWorkingRelated,
});
